import { useState, useCallback, useRef, useEffect } from 'react';
import { Play, RefreshCw, AlertCircle, Eye, Braces, Cog } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import Editor, { type OnMount, type Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { apiClient } from '@/api/client';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { useToastStore } from '@/stores/toast-store';
import { EDITOR_THEMES } from '@/config/themes';
import type { CodeEditorTab } from '@/types';

interface CodeEditorViewProps {
  tab: CodeEditorTab;
}

function getObjectIcon(objectType: CodeEditorTab['objectType']) {
  switch (objectType) {
    case 'view':
      return <Eye size={14} className="text-accent-teal" />;
    case 'function':
    case 'procedure':
      return <Braces size={14} className="text-accent-purple" />;
  }
}

function getCreateTemplate(objectType: CodeEditorTab['objectType'], schema: string, objectName: string): string {
  switch (objectType) {
    case 'view':
      return `CREATE OR REPLACE VIEW "${schema}"."${objectName}" AS\nSELECT column1, column2\nFROM table_name\nWHERE condition;\n`;
    case 'function':
      return `CREATE OR REPLACE FUNCTION "${schema}"."${objectName}"()\nRETURNS void AS $$\nBEGIN\n  -- function body\nEND;\n$$ LANGUAGE plpgsql;\n`;
    case 'procedure':
      return `CREATE OR REPLACE PROCEDURE "${schema}"."${objectName}"()\nLANGUAGE plpgsql AS $$\nBEGIN\n  -- procedure body\nEND;\n$$;\n`;
  }
}

function getDefinitionEndpoint(objectType: CodeEditorTab['objectType'], connectionId: string, schema: string, name: string): string {
  switch (objectType) {
    case 'view':
      return `/api/schema/${connectionId}/view-definition/${schema}/${name}`;
    case 'function':
    case 'procedure':
      return `/api/schema/${connectionId}/function-definition/${schema}/${name}`;
  }
}

export function CodeEditorView({ tab }: CodeEditorViewProps) {
  const addToast = useToastStore((s) => s.addToast);

  // Settings-driven editor options
  const editorFontSize = useSettingsStore((s) => s.getNumber('editor.fontSize', 12.5));
  const editorFontFamily = useSettingsStore((s) => s.get('editor.fontFamily', "'JetBrains Mono', monospace"));
  const editorLineHeight = useSettingsStore((s) => s.getNumber('editor.lineHeight', 1.65));
  const editorMinimap = useSettingsStore((s) => s.getBool('editor.minimap', false));
  const editorWordWrap = useSettingsStore((s) => s.get('editor.wordWrap', 'on'));
  const editorLineNumbers = useSettingsStore((s) => s.get('editor.lineNumbers', 'on'));
  const editorTabSize = useSettingsStore((s) => s.getNumber('editor.tabSize', 2));
  const appTheme = useAppStore((s) => s.theme);
  const editorThemeId = useSettingsStore((s) =>
    s.get(appTheme === 'dark' ? 'editor.theme.dark' : 'editor.theme.light',
          appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light')
  );

  const [sql, setSql] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [definitionLoaded, setDefinitionLoaded] = useState(false);

  const monacoRef = useRef<Monaco | null>(null);
  const editorInstanceRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const handleRunRef = useRef<() => void>(() => {});

  // Fetch object definition on mount
  useEffect(() => {
    if (tab.mode === 'create') {
      setSql(getCreateTemplate(tab.objectType, tab.schema, tab.objectName));
      setDefinitionLoaded(true);
      return;
    }

    setLoading(true);
    setError(null);
    const endpoint = getDefinitionEndpoint(tab.objectType, tab.connectionId, tab.schema, tab.objectName);
    apiClient
      .get<{ sql: string }>(endpoint)
      .then((res) => {
        setSql(res.sql || `-- Could not retrieve definition for ${tab.objectType} "${tab.schema}"."${tab.objectName}"`);
        setDefinitionLoaded(true);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load definition';
        setError(msg);
        // Provide a fallback template so the user can still work
        setSql(getCreateTemplate(tab.objectType, tab.schema, tab.objectName));
        setDefinitionLoaded(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tab.connectionId, tab.schema, tab.objectName, tab.objectType, tab.mode]);

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: (sqlToRun: string) =>
      apiClient.post<{ affectedRows?: number; durationMs?: number }>('/api/query/execute', {
        connectionId: tab.connectionId,
        sql: sqlToRun,
      }),
    onSuccess: () => {
      setError(null);
      addToast('success', `${tab.objectType === 'view' ? 'View' : tab.objectType === 'function' ? 'Function' : 'Procedure'} executed successfully`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Execution failed';
      setError(msg);
    },
  });

  const handleRun = useCallback(() => {
    if (!sql.trim()) return;
    executeMutation.mutate(sql);
  }, [sql, executeMutation]);

  // Keep ref current for Monaco keybinding
  handleRunRef.current = handleRun;

  const handleRefresh = useCallback(() => {
    if (tab.mode === 'create') {
      setSql(getCreateTemplate(tab.objectType, tab.schema, tab.objectName));
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    const endpoint = getDefinitionEndpoint(tab.objectType, tab.connectionId, tab.schema, tab.objectName);
    apiClient
      .get<{ sql: string }>(endpoint)
      .then((res) => {
        setSql(res.sql || '');
        addToast('info', 'Definition refreshed');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to refresh definition';
        setError(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tab.connectionId, tab.schema, tab.objectName, tab.objectType, tab.mode, addToast]);

  const handleEditorMount: OnMount = useCallback(
    (editorInstance, monacoInstance) => {
      monacoRef.current = monacoInstance;
      editorInstanceRef.current = editorInstance;

      // Register all editor themes
      for (const theme of EDITOR_THEMES) {
        monacoInstance.editor.defineTheme(theme.id, theme.monaco);
      }
      monacoInstance.editor.setTheme(editorThemeId);

      // Ctrl/Cmd+Enter to execute
      editorInstance.addAction({
        id: 'dbgo-run-code',
        label: 'Execute',
        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Enter],
        run: () => {
          handleRunRef.current();
        },
      });
    },
    [editorThemeId],
  );

  // Apply editor theme changes dynamically
  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco?.editor) {
      monaco.editor.setTheme(editorThemeId);
    }
  }, [editorThemeId]);

  const handleEditorChange = useCallback((value: string | undefined) => {
    setSql(value ?? '');
  }, []);

  const qualifiedName = `${tab.schema}.${tab.objectName}`;
  const isExecuting = executeMutation.isPending;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-dbgo-toolbar border-b border-border-primary flex-shrink-0">
        <div className="flex items-center gap-1.5 mr-2">
          {getObjectIcon(tab.objectType)}
          <span className="text-xs font-medium text-text-primary truncate max-w-[300px]">
            {qualifiedName}
          </span>
          <span className="text-[10px] text-text-muted px-1.5 py-0.5 bg-dbgo-badge rounded">
            {tab.mode === 'create' ? 'CREATE' : 'ALTER'}
          </span>
        </div>

        <div className="flex-1" />

        <button
          onClick={handleRun}
          disabled={isExecuting || !sql.trim()}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-accent-green/15 text-accent-green rounded hover:bg-accent-green/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Execute (Ctrl+Enter)"
        >
          <Play size={12} />
          {isExecuting ? 'Executing...' : 'Execute'}
        </button>

        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-text-secondary rounded hover:bg-dbgo-hover transition-colors"
          title="Refresh definition"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error bar */}
      {error && (
        <div className="flex items-start gap-2 px-3 py-2 bg-accent-red/10 border-b border-accent-red/20 text-accent-red text-xs">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span className="break-all">{error}</span>
        </div>
      )}

      {/* Loading overlay */}
      {loading && !definitionLoaded && (
        <div className="flex items-center justify-center flex-1">
          <div className="flex items-center gap-2 text-text-muted text-sm">
            <Cog size={16} className="animate-spin" />
            Loading definition...
          </div>
        </div>
      )}

      {/* Editor */}
      {definitionLoaded && (
        <div className="flex-1 min-h-0">
          <Editor
            language="sql"
            theme="vs-dark"
            value={sql}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: editorMinimap },
              fontSize: editorFontSize,
              lineHeight: editorLineHeight * editorFontSize,
              fontFamily: editorFontFamily,
              tabSize: editorTabSize,
              padding: { top: 8, bottom: 8 },
              scrollBeyondLastLine: false,
              renderLineHighlight: 'line',
              lineNumbers: editorLineNumbers as 'on' | 'off' | 'relative',
              lineNumbersMinChars: 3,
              glyphMargin: false,
              folding: true,
              wordWrap: editorWordWrap as 'on' | 'off' | 'wordWrapColumn' | 'bounded',
              automaticLayout: true,
            }}
          />
        </div>
      )}
    </div>
  );
}
